import React from "react";
import { Button, Divider } from "@material-ui/core";
import { Typography, Box, Card, CardContent } from "@material-ui/core";
import { Warning, Error as ErrorIcon, Cancel, CheckCircle } from "@material-ui/icons";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import { Formik, Form } from "formik";
import { TextField } from "../Forms/fields/TextField";
import { EjiService, Job, VehicleInfo, VehicleType } from "../../generated/nest-graphql";
import classNames from "classnames";
import * as Yup from "yup";
import {
  getConditionColorGrade,
  getPadsThicknessColorGrade,
  getRotorsThicknessColorGrade,
  getFluidLevelColorGrade,
  getFluidPpmColorGrade,
  getBatteryHealthGrade,
  getCCAGrade,
} from "./colorGradeHelpers";

export type ServiceApprovalFormValues = {
  customerContacted: string;
  serviceSelections: (boolean | null)[];
};

const serviceApprovalFormValidationSchema = Yup.object().shape({
  customerContacted: Yup.string().required("Required"),
});

export type ServiceApprovalFormProps = {
  services: EjiService[];
  onSubmitApprovals?: (values: ServiceApprovalFormValues) => void;
  initialValues: ServiceApprovalFormValues;
  vehicleInfo?: VehicleInfo;
};

export type RecommendationReason = {
  text: string;
  colorGrade?: "Red" | "Yellow" | "Green" | null;
};

export const getServiceRecommendationReasons = (
  service: EjiService,
  vehicleInfo?: VehicleInfo
): RecommendationReason[] => {
  const reasons: RecommendationReason[] = [];
  const checklistInfo = service.diagnosticInfo?.checklistInfo as any;

  if (!checklistInfo) {
    return reasons;
  }

  if (checklistInfo.condition && Array.isArray(checklistInfo.condition)) {
    const conditionColorGrade = getConditionColorGrade(checklistInfo.condition);
    checklistInfo.condition.forEach((condition: string) => {
      reasons.push({
        text: condition,
        colorGrade: conditionColorGrade,
      });
    });
  }

  if (checklistInfo.innerPadThickness) {
    const colorGrade = getPadsThicknessColorGrade(checklistInfo.innerPadThickness);
    reasons.push({
      text: `Inner Pad Thickness: ${checklistInfo.innerPadThickness}`,
      colorGrade,
    });
  }

  if (checklistInfo.outerPadThickness) {
    const colorGrade = getPadsThicknessColorGrade(checklistInfo.outerPadThickness);
    reasons.push({
      text: `Outer Pad Thickness: ${checklistInfo.outerPadThickness}`,
      colorGrade,
    });
  }

  if (checklistInfo.padsThickness) {
    const colorGrade = getPadsThicknessColorGrade(checklistInfo.padsThickness);
    reasons.push({
      text: `Pads Thickness: ${checklistInfo.padsThickness}`,
      colorGrade,
    });
  }

  if (checklistInfo.thicknessInches) {
    const discardThickness =
      vehicleInfo?.frontRotorsDiscardThicknessInches?.toString() ||
      vehicleInfo?.rearRotorsDiscardThicknessInches?.toString() ||
      null;
    const colorGrade = getRotorsThicknessColorGrade(checklistInfo.thicknessInches, discardThickness);
    reasons.push({
      text: `Thickness: ${checklistInfo.thicknessInches} inches`,
      colorGrade,
    });
  }

  if (checklistInfo.driverSide) {
    const colorGrade = getPadsThicknessColorGrade(checklistInfo.driverSide);
    reasons.push({
      text: `Driver Side: ${checklistInfo.driverSide} mm`,
      colorGrade,
    });
  }

  if (checklistInfo.passengerSide) {
    const colorGrade = getPadsThicknessColorGrade(checklistInfo.passengerSide);
    reasons.push({
      text: `Passenger Side: ${checklistInfo.passengerSide} mm`,
      colorGrade,
    });
  }

  if (checklistInfo.level) {
    const colorGrade = getFluidLevelColorGrade(checklistInfo.level);
    reasons.push({
      text: `Level: ${checklistInfo.level}`,
      colorGrade,
    });
  }

  if (checklistInfo.ppm) {
    const colorGrade = getFluidPpmColorGrade(checklistInfo.ppm);
    reasons.push({
      text: `PPM: ${checklistInfo.ppm}`,
      colorGrade,
    });
  }

  if (checklistInfo.batteryHealth) {
    const colorGrade = getBatteryHealthGrade(checklistInfo.batteryHealth);
    reasons.push({
      text: `${checklistInfo.batteryHealth} %`,
      colorGrade,
    });
  }

  if (checklistInfo.coldCrankingAmps) {
    const CCA = vehicleInfo?.CCA?.toString() || null;
    const colorGrade = CCA ? getCCAGrade(checklistInfo.coldCrankingAmps, CCA) : null;
    reasons.push({
      text: `Cold Cranking Amps: ${checklistInfo.coldCrankingAmps}`,
      colorGrade,
    });
  }

  return reasons.filter((reason) => reason.colorGrade !== "Green");
};

export const ServiceApprovalForm: React.FC<ServiceApprovalFormProps> = ({
  services,
  onSubmitApprovals,
  initialValues,
  vehicleInfo,
}) => {
  return (
    <Formik<ServiceApprovalFormValues>
      initialValues={initialValues}
      validationSchema={serviceApprovalFormValidationSchema}
      onSubmit={(values) => {
        if (onSubmitApprovals) {
          onSubmitApprovals(values);
        }
      }}
    >
      {({ values, setFieldValue, isValid, errors }) => (
        <Form>
          <div className="flex flex-col gap-6 p-4">
            <div>
              <TextField name="customerContacted" label="Approval Point of Contact" placeholder="e.g. John Smith" />
            </div>

            {services.map((service, index) => {
              const diagnosticCategory = service.diagnosticInfo?.diagnosticCategory;
              console.log("diagnosticCategory", diagnosticCategory);
              const approvalStatus = service.approvalStatus;
              const recommendationReasons = getServiceRecommendationReasons(service, vehicleInfo);

              const getIcon = () => {
                if (diagnosticCategory === "Recommended Immediately") {
                  return <ErrorIcon color="error" />;
                }
                if (diagnosticCategory === "Service Soon") {
                  return <Warning htmlColor="#ff9800" />;
                }
                if (diagnosticCategory === "All Systems Go") {
                  return <CheckCircle htmlColor="#00C853" />;
                }
                return null;
              };

              const getStatusText = () => {
                if (approvalStatus === "Approved") return "Approved";
                if (approvalStatus === "Not Approved") return "Not Approved";
                if (approvalStatus === "Pending") return "Pending";
                return null;
              };

              return (
                <Card key={index} variant="outlined">
                  <CardContent>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-2">
                        {getIcon()}
                        <Typography variant="h6">
                          <Box fontWeight="fontWeightBold">{service.name}</Box>
                        </Typography>
                      </div>

                      {recommendationReasons.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {recommendationReasons.map((reason, idx) => {
                            return (
                              <React.Fragment key={idx}>
                                <Typography
                                  variant="body1"
                                  className={classNames({
                                    "text-red-500": reason.colorGrade === "Red",
                                    "text-yellow-500": reason.colorGrade === "Yellow",
                                    "text-green-500": reason.colorGrade === "Green",
                                  })}
                                >
                                  {reason.text}
                                </Typography>
                                {idx < recommendationReasons.length - 1 && (
                                  <Divider orientation="vertical" flexItem style={{ background: "#fff" }} />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}

                      {service.diagnosticInfo?.checklistInfo?.notes && (
                        <Typography variant="body2" color="textSecondary">
                          {service.diagnosticInfo?.checklistInfo?.notes}
                        </Typography>
                      )}

                      <div className="w-full">
                        <ToggleButtonGroup
                          value={values.serviceSelections[index]}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) {
                              const newSelections = [...values.serviceSelections];
                              newSelections[index] = newValue;
                              setFieldValue("serviceSelections", newSelections);
                            }
                          }}
                          style={{ width: "100%" }}
                        >
                          <ToggleButton
                            value={false}
                            style={{
                              backgroundColor: values.serviceSelections[index] === false ? "#f50057" : "#0000",
                              color: "white",
                              flex: 1,
                            }}
                          >
                            <Cancel style={{ marginRight: 8 }} />
                            Decline
                          </ToggleButton>
                          <ToggleButton
                            value={true}
                            style={{
                              backgroundColor: values.serviceSelections[index] === true ? "#4caf50" : "#0000",
                              color: "white",
                              flex: 1,
                            }}
                          >
                            <CheckCircle style={{ marginRight: 8 }} />
                            Approve
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            <div className="flex gap-4">
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={!isValid}>
                Submit Approvals
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
